import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { ContextBar } from "../components/context-bar";
import { NO_DISCORD_CHANGES, HUMAN_APPROVAL_REQUIRED } from "../components/onboarding";
import {
  CaseList,
  CaseDetail,
  TakeActionForm,
  MemberWarningsView,
  AutomodBuilder,
  LoggingConfigForm,
  LOCKED_PIPELINE_NOTE,
} from "../components/moderation";
import { useSession } from "../hooks/useSession";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  buildAutomodContent,
  buildLoggingContent,
  type AutomodModule,
  type LoggingModule,
  type ModerationCase,
  type ModerationCaseDetail,
  type TakeActionInput,
  type WarningSummary,
} from "../lib/api";

type Tab = "console" | "member" | "automod" | "logging";

const TABS: { id: Tab; label: string }[] = [
  { id: "console", label: "Moderation console" },
  { id: "member", label: "Member warnings" },
  { id: "automod", label: "Automod rules" },
  { id: "logging", label: "Logging config" },
];

function toMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Please try again.";
}

/**
 * Phase 6B moderation / automod / logging admin surface. Server-driven and
 * honest: every value comes from the control API, a moderation action is
 * requested through the LOCKED pipeline (no live Discord effect), and every
 * deployment of automod / logging config is a hand-off to Phase 4 approval.
 */
export function Moderation(): JSX.Element {
  const { state } = useSession();
  const ctx = useAppContext();
  const [tab, setTab] = useState<Tab>("console");

  if (state.kind === "loading") return <LoadingState label="Checking your session…" />;
  if (state.kind === "not-configured")
    return (
      <NotConfiguredState title="Control API URL not configured">
        Set <code>VITE_CONTROL_API_URL</code> to manage moderation.
      </NotConfiguredState>
    );
  if (state.kind === "unavailable") return <ApiUnavailableState />;
  if (state.kind === "anonymous")
    return (
      <>
        <PageHeader title="Moderation" />
        <EmptyState title="Please sign in to continue">
          <Link to="/login">
            <Button variant="primary">Sign in with Discord</Button>
          </Link>
        </EmptyState>
      </>
    );

  const ready = Boolean(ctx.tenantId && ctx.guildId);

  return (
    <>
      <PageHeader title="Moderation, automod & logging">
        <p>
          Case management, member warnings, structured automod rules and operational logging. {NO_DISCORD_CHANGES} {HUMAN_APPROVAL_REQUIRED}
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to manage moderation.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar requireGuild />
          {!ready ? (
            <EmptyState title="Select a workspace and server">
              Choose a workspace and enter your ODIN server id above to manage moderation.
            </EmptyState>
          ) : (
            <>
              <div className="row ob-tabs" role="tablist" aria-label="Moderation sections" style={{ gap: "0.5rem", margin: "1rem 0" }}>
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={tab === t.id}
                    className={`btn ${tab === t.id ? "btn--primary" : "btn--secondary"}`}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === "console" ? <ConsoleTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
              {tab === "member" ? <MemberTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
              {tab === "automod" ? <AutomodTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
              {tab === "logging" ? <LoggingTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
            </>
          )}
        </>
      )}
    </>
  );
}

function ConsoleTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [cases, setCases] = useState<ModerationCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ModerationCaseDetail | null>(null);
  const [detailMsg, setDetailMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionResult, setActionResult] = useState<string | null>(null);

  const loadCases = () => {
    api
      .moderation.listCases(tenantId, guildId)
      .then((r) => {
        setCases(r.cases);
        setError(null);
      })
      .catch((e) => setError(toMessage(e)));
  };

  useEffect(() => {
    loadCases();
    // loadCases reads the same tenant/guild props it depends on
  }, [tenantId, guildId]);

  const selectCase = (caseId: string) => {
    setSelectedId(caseId);
    setDetail(null);
    setDetailMsg(null);
    api
      .moderation.getCase(tenantId, guildId, caseId)
      .then(setDetail)
      .catch((e) => setDetailMsg(toMessage(e)));
  };

  const takeAction = async (input: TakeActionInput) => {
    setBusy(true);
    setActionResult(null);
    try {
      const r = await api.moderation.takeAction(tenantId, guildId, input);
      setActionResult(
        `Recorded ${r.action.actionType} on case ${r.case.id} — status ${r.case.status}, result ${r.action.result}. ` +
          `Active warnings: ${r.activeWarnings}. No live Discord change was made (locked pipeline).`,
      );
      loadCases();
    } catch (e) {
      setActionResult(`Not recorded: ${toMessage(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const transition = async (verb: "appeal" | "overturn" | "close", caseId: string) => {
    setBusy(true);
    try {
      const fn =
        verb === "appeal" ? api.moderation.appealCase : verb === "overturn" ? api.moderation.overturnCase : api.moderation.closeCase;
      const r = await fn(tenantId, guildId, caseId);
      setDetailMsg(`Case is now ${r.case.status}. Records updated — no live Discord change was made.`);
      loadCases();
      selectCase(caseId);
    } catch (e) {
      setDetailMsg(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ob-builder">
      <div className="dryrun-banner" role="note">
        <strong>Locked pipeline.</strong>
        <span>{LOCKED_PIPELINE_NOTE}</span>
      </div>

      <TakeActionForm onSubmit={takeAction} busy={busy} result={actionResult} />

      {error ? (
        <ApiUnavailableState detail={error} />
      ) : cases === null ? (
        <LoadingState label="Reading moderation cases…" />
      ) : (
        <CaseList cases={cases} selectedId={selectedId} onSelect={selectCase} />
      )}

      {selectedId && detail ? (
        <CaseDetail
          detail={detail}
          busy={busy}
          message={detailMsg}
          onAppeal={(id) => transition("appeal", id)}
          onOverturn={(id) => transition("overturn", id)}
          onClose={(id) => transition("close", id)}
        />
      ) : selectedId && detailMsg ? (
        <ApiUnavailableState detail={detailMsg} />
      ) : null}
    </div>
  );
}

function MemberTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [subject, setSubject] = useState("");
  const [summary, setSummary] = useState<WarningSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      setSummary(await api.moderation.getWarnings(tenantId, guildId, id));
    } catch (e) {
      setError(toMessage(e));
      setSummary(null);
    } finally {
      setBusy(false);
    }
  };

  const expire = async (warningId: string) => {
    setBusy(true);
    try {
      await api.moderation.expireWarning(tenantId, guildId, warningId);
      if (subject.trim()) await load(subject.trim());
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ob-builder">
      <div className="context-bar">
        <div className="field">
          <span className="field__label">Member Discord user id</span>
          <div className="row" style={{ gap: "0.5rem" }}>
            <input className="field__input" value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Member Discord user id" placeholder="e.g. 123456789012345678" />
            <Button variant="secondary" disabled={busy || subject.trim().length < 1} onClick={() => load(subject.trim())}>
              Load warnings
            </Button>
          </div>
        </div>
      </div>

      {error ? <ApiUnavailableState detail={error} /> : null}
      {summary ? (
        <MemberWarningsView summary={summary} onExpire={expire} busy={busy} />
      ) : !error ? (
        <EmptyState title="Enter a member id">Enter a Discord user id above to view active warnings and the escalation recommendation.</EmptyState>
      ) : null}
    </div>
  );
}

function AutomodTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onPreview = async (module: AutomodModule, sample: { content: string }) =>
    api.moderation.previewAutomod(tenantId, guildId, { content: buildAutomodContent(module), sample });

  const onGenerateDraft = async (module: AutomodModule) => {
    setError(null);
    setResult(null);
    try {
      const version = await api.moderation.createAutomodDraft(tenantId, guildId, buildAutomodContent(module));
      const validation = await api.moderation.validateAutomodVersion(tenantId, guildId, version.id);
      setResult(
        validation.valid
          ? `Draft version ${version.id} (v${version.version}) created and validated. ${NO_DISCORD_CHANGES} ${HUMAN_APPROVAL_REQUIRED}`
          : `Draft version ${version.id} created, but validation found ${validation.errors.length} error(s): ${validation.errors.map((e) => `${e.path}: ${e.message}`).join("; ")}`,
      );
    } catch (e) {
      setError(toMessage(e));
    }
  };

  const onValidateRemote = async (module: AutomodModule) => {
    setError(null);
    setResult(null);
    try {
      const version = await api.moderation.createAutomodDraft(tenantId, guildId, buildAutomodContent(module));
      const validation = await api.moderation.validateAutomodVersion(tenantId, guildId, version.id);
      setResult(validation.valid ? `Server validation passed for draft v${version.version}. ${NO_DISCORD_CHANGES}` : `Server validation failed: ${validation.errors.map((e) => e.message).join("; ")}`);
    } catch (e) {
      setError(toMessage(e));
    }
  };

  return (
    <>
      {error ? <ApiUnavailableState detail={error} /> : null}
      {result ? (
        <div className="state" role="status" style={{ marginBottom: "1rem" }}>
          <p>{result}</p>
        </div>
      ) : null}
      <AutomodBuilder onPreview={onPreview} onGenerateDraft={onGenerateDraft} onValidateRemote={onValidateRemote} />
    </>
  );
}

function LoggingTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [initial, setInitial] = useState<LoggingModule | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .moderation.getLoggingConfig(tenantId, guildId)
      .then((r) => {
        if (!active) return;
        if (r.configuration) {
          setInitial({
            enabled: r.configuration.enabled,
            schemaVersion: "1",
            logChannelKey: r.configuration.logChannelKey ?? undefined,
            categories: r.configuration.categories,
            redactContent: r.configuration.redactContent,
          });
        }
        setLoaded(true);
      })
      .catch((e) => {
        if (active) {
          setError(toMessage(e));
          setLoaded(true);
        }
      });
    return () => {
      active = false;
    };
  }, [tenantId, guildId]);

  const onValidateRemote = async (module: LoggingModule) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.moderation.saveLoggingDraft(tenantId, guildId, buildLoggingContent(module));
      const validation = await api.moderation.validateLoggingDraft(tenantId, guildId, buildLoggingContent(module));
      setMessage(validation.valid ? `Draft saved and validated. ${NO_DISCORD_CHANGES}` : `Validation found ${validation.errors.length} error(s): ${validation.errors.map((e) => `${e.path}: ${e.message}`).join("; ")}`);
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onActivate = async (module: LoggingModule) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.moderation.saveLoggingDraft(tenantId, guildId, buildLoggingContent(module));
      const activated = await api.moderation.activateLogging(tenantId, guildId, {});
      setMessage(`Logging configuration activated (${activated.categories.length} categor(y/ies)). ${HUMAN_APPROVAL_REQUIRED} It binds to a deployment that already cleared Phase 4.`);
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <LoadingState label="Reading logging configuration…" />;

  return (
    <>
      {error ? <ApiUnavailableState detail={error} /> : null}
      <LoggingConfigForm initial={initial} onValidateRemote={onValidateRemote} onActivate={onActivate} message={message} busy={busy} />
    </>
  );
}
